import { Component, effect, inject, input, OnDestroy, output } from '@angular/core';
import { AuthService } from '../../core/security/auth.service';
import { Router } from '@angular/router';
import { MessagingGroupModel } from '../../api/models/messaging-group-model';
import { ContactLookupModel } from '../../api/models';
import { HeaderService } from '../../core/services/header.service';
import { ProMessagingService } from '../pro-messaging.service';
import { DateTime } from 'luxon';
import { HumanizedDurationPipe } from '../../core/pipes/humanized-duration.pipe';
import { PhoneNumbersUtils } from '../../core/utilities/phone-numbers-helpers';
import { FlexModule } from '@angular/flex-layout';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatMiniFabAnchor, MatIconButton, MatIconAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatBadge } from '@angular/material/badge';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';
import { HighlightPipe } from '../../core/pipes/highlight.pipe';
import { HumanizedDatePipe } from '../../core/pipes/humanized-date.pipe';
import { InitialsPipe } from '../../core/pipes/initials.pipe';
import { NameInitialsPipe } from '../../core/pipes/name-initials.pipe';

@Component({
    selector: 'app-pro-phone-number-group-item-v2',
    template: `
      @if (!dataItem) {
        <div class="item-container" fxLayout="row" fxLayoutAlign="start center">
          <div class="item-avatar">
            <mat-spinner diameter="32"></mat-spinner>
          </div>
          <div class="item-content">
            <div class="item-header">
              <h4 class="item-heading">Loading...</h4>
            </div>
          </div>
        </div>
      } @else {
        <div class="item-container" fxLayout="row" fxLayoutAlign="start stretch">
          <div class="item-avatar" fxLayout="column" fxLayoutAlign="start center">
            @if (primaryContact?.name) {
              <a mat-mini-fab class="button-32 mat-elevation-z0 unknown-user">
                <span>{{ primaryContact.name | initials }}</span>
              </a>
            } @else {
              <a mat-mini-fab class="button-32 mat-elevation-z0 unknown-user">
                <mat-icon>perm_identity</mat-icon>
              </a>
            }
            <button mat-icon-button class="button-32 pin-conversation-btn" (click)="onPinClicked(dataItem())">
              @if (!dataItem().pin && !(pinUpdateInProgress().inProgress && pinUpdateInProgress().threadKey === dataItem().threadKey)) {
                <mat-icon fontSet="material-icons-outlined" matTooltip="Pin conversation in top of the list">
                  push_pin
                </mat-icon>
              }
              @if (dataItem().pin && !(pinUpdateInProgress().inProgress && pinUpdateInProgress().threadKey === dataItem().threadKey)) {
                <mat-icon fontSet="material-icons" color="primary" matTooltip="Unpin conversation">
                  push_pin
                </mat-icon>
              }
              @if (pinUpdateInProgress().inProgress && pinUpdateInProgress().threadKey === dataItem().threadKey) {
                <mat-spinner mode="indeterminate" diameter="24" strokeWidth="2"></mat-spinner>
              }
            </button>
          </div>
          <div class="item-content" fxFlex fxLayout="column" fxLayoutAlign="space-between stretch">
            <div class="item-header">
              <h4 class="item-heading">
                <span class="item-contact">
                  {{ primaryContact?.name || primaryContact?.phoneNumber | phoneNumber }}
                  @if (hasContactsModule && hasContactsEditRight && primaryContact?.id === '0') {
                    <a mat-icon-button class="button-32" matTooltip="Add to contacts"
                       (click)="addNewContact($event, primaryContact)">
                      <mat-icon fontSet="material-icons-outlined">person_add</mat-icon>
                    </a>
                  }
                </span>
                @if (primaryContact?.name) {
                  <span class="item-phone">{{ primaryContact?.phoneNumber | phoneNumber }}</span>
                }
              </h4>
            </div>
            <div class="item-last-message" fxLayout="row" fxLayoutAlign="start center">
              <p [innerHTML]="dataItem().lastMessage || 'This message was deleted' | highlight: searchText()"></p>
            </div>
          </div>
          <div class="right-holder" fxLayout="column" fxLayoutAlign="start end">
            <time class="item-time">
              {{ dataItem().lastTime | humanizedDate }}
            </time>
            @if (dataItem().wabaConvId) {
              <time class="item-time">
                {{ expiresIn }}
              </time>
              <div>
                <i class="fa-brands fa-whatsapp messaging-type-waba"
                   style="font-size: 24px"
                   matTooltip="WhatsApp conversation">
                </i>
              </div>
            }
            @if (dataItem().isBroadcast) {
              <div>
                <i class="material-icons-outlined" matTooltip="Broadcast conversation">group</i>
              </div>
            }
            @if (dataItem().isGroup) {
              <div>
                <i class="material-icons" matTooltip="Group conversation">group</i>
              </div>
            }
            @if (dataItem().unread > 0) {
              <div
                class="item-badge"
                [matBadge]="dataItem().unread"
                matBadgePosition="before"
                matBadgeColor="warn">
              </div>
            }
          </div>
        </div>
      }
    `,
    styleUrls: ['./pro-phone-number-group-item-v2.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        MatProgressSpinner,
        MatMiniFabAnchor,
        MatIcon,
        MatIconButton,
        MatTooltip,
        MatIconAnchor,
        MatBadge,
        PhoneNumberPipe,
        HighlightPipe,
        HumanizedDatePipe,
        InitialsPipe,
        NameInitialsPipe,
    ],
})
export class ProPhoneNumberGroupItemV2Component implements OnDestroy {
  dataItem = input<MessagingGroupModel>();
  wabaConvExp = input<string>();
  searchText= input<string>();
  hasContactsModule = input<boolean>(false);
  hasContactsEditRight = input<boolean>(false);
  pinUpdateInProgress = input({ threadKey: '', inProgress: false })
  pinChangedEvent = output<MessagingGroupModel>()

  primaryContact: ContactLookupModel;

  humanizedDurationPipe = new HumanizedDurationPipe();
  expiresIn = '';
  expiresInTimeout: any;

  authService = inject(AuthService);
  headerService = inject(HeaderService);
  messagingService = inject(ProMessagingService);
  private _router = inject(Router);

  constructor() {
    effect(() => {
      if (this.dataItem().contacts && this.dataItem().contacts.length > 0) {
        this.primaryContact = this.dataItem().contacts[0];
      }

      if (this.dataItem && this.dataItem().rowNumber === -99) {
        this.updateContactInformationFromServer();
      }

      this.updateConversationExpiration();
    })
  }

  updateConversationExpiration() {
    this.expiresIn = 'Expired';
    if (this.dataItem) {
      if (this.dataItem().wabaConvExp) {
        const expDate = DateTime.fromISO(this.dataItem().wabaConvExp);
        const now = DateTime.local();
        if (expDate > now) {
          this.expiresIn = `Expires in ${this.humanizedDurationPipe.transform(this.dataItem().wabaConvExp)}`;

          if (this.expiresInTimeout) {
            clearTimeout(this.expiresInTimeout);
          }
          this.expiresInTimeout = setTimeout(() => {
            this.updateConversationExpiration();
          }, 30000);
        }
      }
    }
  }

  ngOnDestroy() {
    clearTimeout(this.expiresInTimeout);
  }

  addNewContact($event: MouseEvent, primaryContact: ContactLookupModel) {
    $event.preventDefault();
    this._router.navigate([this.headerService.themePrefix, 'contacts', 'contact', 'new', 'edit'], {
      queryParams: { phoneNumber: primaryContact?.phoneNumber },
    });
  }

  async onPinClicked(dataItem: MessagingGroupModel) {
    this.pinChangedEvent.emit(dataItem);
  }

  private updateContactInformationFromServer() {
    console.log('updateContactInformationFromServer');
    for (const contact of this.dataItem().contacts) {
      const phoneNumberNational = PhoneNumbersUtils.phoneNumberToNational(contact.phoneNumber);
      this.messagingService.getContactsAndTagsObservable(phoneNumberNational).subscribe(contacts => {
        if (contacts.length > 0) {
          const contactInfo = contacts[0];
          contact.phoneNumber = contactInfo.phoneNumber;
          contact.name = contactInfo.name;
          contact.id = contactInfo.contactId;
          contact.phoneType = 'Contacts';
          contact.communicationType = contactInfo.communicationType;
          contact.isOptedIn = contactInfo.isOptedIn;
        }
      });
    }
  }
}
